import { createQueryOptions, useTransport } from "@connectrpc/connect-query";
import { useQuery } from "@tanstack/react-query";
import { AvailabilityService } from "@viajero/proto/guest/v1";
import type { AvailabilityVisit } from "@/features/shops/lib/availabilityVisit";
import { isValidId } from "@/lib/id";

// 店舗画面の空席確認状態。unconfigured は予約条件 (日時・人数) が揃わず確認していない状態。
export type AvailabilityStatus = "unconfigured" | "loading" | "available" | "unavailable" | "error";

// 空席確認結果は Bot 同期 Invoke で重いため、条件をキーにキャッシュする。ただしキャッシュが
// 際限なく増えないよう、GC 時間には上限 (30 分) を設ける (concierge の useShopAvailabilities と同方針)。
const AVAILABILITY_GC_TIME_MS = 1000 * 60 * 30;

/**
 * useCheckAvailability は店舗画面の予約希望日時・人数で CheckAvailability を呼び出し、
 * 確認状態を返す (JPTP_CANSEI-1443)。
 *
 * - 空席確認は予約サイト操作 Bot を同期 Invoke する重い処理のため、条件 (店舗・日時・人数) を
 *   キーに TanStack Query でキャッシュし (staleTime 無限)、同一条件での再フェッチを避ける。
 * - 予約条件が揃わない (visit=null) 間はクエリを発行せず unconfigured を返す。
 * - 「空席なし」は正常応答 (available=false)。Bot 操作の失敗・判定不能は error (retry しない)。
 * - 案件アクセストークンは transport の interceptor が x-case-token ヘッダで送る。
 *   空席は案件に依存しない (店舗・日時・人数で決まる) ため、queryKey に caseToken は含めない。
 */
export function useCheckAvailability(
  shopId: string,
  visit: AvailabilityVisit | null,
): AvailabilityStatus {
  const transport = useTransport();
  const enabled = visit !== null && isValidId(shopId);
  const query = useQuery({
    ...createQueryOptions(
      AvailabilityService.method.checkAvailability,
      {
        shopId: isValidId(shopId) ? BigInt(shopId) : 0n,
        date: visit?.date ?? "",
        time: visit?.time ?? "",
        partySize: visit?.partySize ?? 0,
      },
      { transport },
    ),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: AVAILABILITY_GC_TIME_MS,
    retry: false,
    enabled,
  });

  if (!enabled) return "unconfigured";
  if (query.isError) return "error";
  if (query.data !== undefined) return query.data.available ? "available" : "unavailable";
  return "loading";
}
